import { createReportMapImage } from "./report-map.js";

const COLORS = {
  ink: [21, 36, 29],
  muted: [93, 105, 99],
  forest: [30, 100, 69],
  forestDark: [23, 63, 45],
  sage: [233, 240, 236],
  wash: [247, 249, 248],
  line: [215, 223, 218],
  attention: [160, 60, 43],
  watch: [154, 104, 22],
  verify: [55, 108, 117],
  clear: [44, 117, 82],
  unknown: [104, 113, 108],
  white: [255, 255, 255],
  paleText: [212, 226, 218],
  sageText: [188, 224, 204]
};

const PAGE = { width: 612, height: 792, left: 52, right: 52, top: 58, bottom: 52 };
const CONTENT_WIDTH = PAGE.width - PAGE.left - PAGE.right;
const REVIEW_URL = "https://landsageinsights.com/#contact";

export function prefersPdfPreview({ userAgent = "", maxTouchPoints = 0 } = {}) {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

export function pdfFilename(snapshot) {
  const address = clean(snapshot?.property?.matchedAddress || "property")
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70);
  return "LandSage-Property-Scanner-" + (address || "report") + ".pdf";
}

export async function createSnapshotPdf(snapshot, { mapImage: suppliedMapImage } = {}) {
  if (!snapshot?.property?.matchedAddress) throw new Error("A completed property scan is required.");
  const [pdfLib, mapImage] = await Promise.all([
    import("pdf-lib"),
    suppliedMapImage ? Promise.resolve(suppliedMapImage) : createReportMapImage(snapshot)
  ]);
  const pdf = await pdfLib.PDFDocument.create();
  const fonts = {
    regular: await pdf.embedFont(pdfLib.StandardFonts.Helvetica),
    bold: await pdf.embedFont(pdfLib.StandardFonts.HelveticaBold)
  };
  const ctx = { pdf, fonts, rgb: pdfLib.rgb, PDFName: pdfLib.PDFName, PDFString: pdfLib.PDFString };

  drawCover(ctx, snapshot);
  await drawMapAndProperty(ctx, snapshot, mapImage);
  drawFindings(ctx, snapshot);
  drawInvestigation(ctx, snapshot);
  drawSourcesAndLimitations(ctx, snapshot);
  addPageFurniture(ctx, snapshot);

  const bytes = await pdf.save({ useObjectStreams: false });
  return new Blob([bytes], { type: "application/pdf" });
}

function drawCover(ctx, snapshot) {
  const page = newPage(ctx);
  drawRect(ctx, page, 0, 0, PAGE.width, 214, COLORS.forestDark);
  drawRect(ctx, page, PAGE.left, 47, 38, 38, COLORS.forest);
  drawText(ctx, page, "LS", PAGE.left + 19, 60, 10, "bold", COLORS.white, { align: "center" });
  drawText(ctx, page, "LANDSAGE INSIGHTS", PAGE.left + 52, 54, 12, "bold", COLORS.white);
  drawText(ctx, page, "PROPERTY SCANNER", PAGE.left + 52, 71, 8, "normal", COLORS.sageText);
  drawText(ctx, page, "FREE PRELIMINARY SCREEN", PAGE.width - PAGE.right, 60, 8, "bold", COLORS.sageText, { align: "right" });

  drawText(ctx, page, "PROPERTY DECISION SNAPSHOT", PAGE.left, 252, 10, "bold", COLORS.forest);
  let y = drawLines(ctx, page, snapshot.property.matchedAddress, PAGE.left, 286, CONTENT_WIDTH, 37, 32, "bold", COLORS.ink);

  y += 24;
  const takeawayLines = wrapped(ctx, snapshot.takeaway.title, CONTENT_WIDTH - 42, 19, "bold");
  const summaryLines = wrapped(ctx, snapshot.takeaway.summary, CONTENT_WIDTH - 42, 10.5, "normal");
  const panelHeight = 30 + takeawayLines.length * 23 + summaryLines.length * 15 + 28;
  drawRect(ctx, page, PAGE.left, y, CONTENT_WIDTH, panelHeight, COLORS.sage);
  drawRect(ctx, page, PAGE.left, y, 8, panelHeight, statusColor(snapshot.takeaway.level));
  drawText(ctx, page, clean(snapshot.takeaway.eyebrow).toUpperCase(), PAGE.left + 24, y + 18, 8, "bold", COLORS.forest);
  drawTextLines(ctx, page, takeawayLines, PAGE.left + 24, y + 42, 19, 23, "bold", COLORS.ink);
  const summaryY = y + 42 + takeawayLines.length * 23 + 7;
  drawTextLines(ctx, page, summaryLines, PAGE.left + 24, summaryY, 10.5, 15, "normal", COLORS.muted);

  drawText(ctx, page, "WHAT THIS REPORT IS FOR", PAGE.left, 650, 8, "bold", COLORS.forest);
  drawLines(ctx, page, "Use this snapshot to decide what deserves verification before you advance, pause, renegotiate, redesign, or commit more time and money.", PAGE.left, 670, CONTENT_WIDTH, 15, 10, "normal", COLORS.ink);
  drawRule(ctx, page, PAGE.left, 704, PAGE.width - PAGE.right, COLORS.line);
  drawText(ctx, page, "Screening for an informed next step - not an official determination.", PAGE.left, 720, 9, "bold", COLORS.ink);
  drawText(ctx, page, clean(snapshot.snapshotId) + "  |  " + formatDate(snapshot.generatedAt) + "  |  Version " + clean(snapshot.version), PAGE.left, 740, 8, "normal", COLORS.muted);
}

async function drawMapAndProperty(ctx, snapshot, mapImage) {
  const page = newPage(ctx);
  let y = sectionHeading(ctx, page, "SCREENING MAP", snapshot.property.matchedAddress);
  const mapHeight = mapImage ? 250 : 88;
  drawRect(ctx, page, PAGE.left, y, CONTENT_WIDTH, mapHeight, [245, 247, 245], COLORS.line, 1);

  if (mapImage) {
    try {
      const embedded = await embedDataImage(ctx.pdf, mapImage);
      page.drawImage(embedded, {
        x: PAGE.left + 1,
        y: PAGE.height - y - mapHeight + 1,
        width: CONTENT_WIDTH - 2,
        height: mapHeight - 2
      });
    } catch {
      drawMapFallback(ctx, page, y);
    }
  } else {
    drawMapFallback(ctx, page, y);
  }

  y += mapHeight + 14;
  const caption = snapshot.analysisMode === "verified_parcel"
    ? "Returned overlap areas are approximate screening measurements. Compare the provider boundary with a current survey and official local records."
    : "The dashed circle is a " + number(snapshot.screeningRadiusFeet) + "-foot area around the matched address point. It is not a parcel boundary and cannot establish whether returned mapping affects the property.";
  y = drawLines(ctx, page, caption, PAGE.left, y, CONTENT_WIDTH, 12, 8.5, "normal", COLORS.muted) + 18;
  const wetlands = snapshot.findings?.find(item => item.key === "wetlands");
  if (wetlands?.notice && ["attention", "watch", "unknown"].includes(wetlands.status)) {
    const noticeLines = wrapped(ctx, wetlands.notice, CONTENT_WIDTH - 34, 8.7, "normal");
    const noticeHeight = 31 + noticeLines.length * 11.5;
    drawRect(ctx, page, PAGE.left, y, CONTENT_WIDTH, noticeHeight, [255, 248, 233], [212, 177, 109], 1);
    drawText(ctx, page, wetlands.status === "unknown" ? "WETLAND CHECK UNRESOLVED" : "IMPORTANT WETLAND LIMITATION", PAGE.left + 16, y + 12, 7.7, "bold", COLORS.watch);
    drawTextLines(ctx, page, noticeLines, PAGE.left + 16, y + 28, 8.7, 11.5, "normal", COLORS.ink);
    y += noticeHeight + 17;
  }
  y = smallHeading(ctx, page, "PROPERTY MATCHED", y);

  for (const [label, value] of propertyRows(snapshot)) {
    if (!value) continue;
    drawText(ctx, page, label, PAGE.left, y, 8.5, "normal", COLORS.muted);
    const valueLines = wrapped(ctx, value, 335, 9.5, "bold");
    drawTextLines(ctx, page, valueLines, PAGE.left + 155, y, 9.5, 12, "bold", COLORS.ink);
    y += Math.max(17, valueLines.length * 12);
  }
}

function drawMapFallback(ctx, page, y) {
  drawLines(
    ctx,
    page,
    "The interactive map could not be embedded. Use the online scan and official source links to review mapped locations.",
    PAGE.left + 20,
    y + 28,
    CONTENT_WIDTH - 40,
    14,
    10,
    "normal",
    COLORS.muted
  );
}

function drawFindings(ctx, snapshot) {
  let page = newPage(ctx);
  let y = sectionHeading(ctx, page, "WHAT STANDS OUT", "Review areas identified in this scan.");

  for (const [index, finding] of snapshot.findings.entries()) {
    const layout = findingLayout(ctx, finding);
    const balanceFourCards = snapshot.findings.length === 4 && index === 2;
    if (balanceFourCards || y + layout.height > PAGE.height - PAGE.bottom) {
      page = newPage(ctx);
      y = sectionHeading(ctx, page, "WHAT STANDS OUT - CONTINUED", "Remaining review areas identified in this scan.");
    }
    drawFindingCard(ctx, page, finding, layout, y, index, snapshot.findings.length);
    y += layout.height + 12;
  }
}

function findingLayout(ctx, finding) {
  const width = CONTENT_WIDTH - 40;
  const title = wrapped(ctx, finding.title, width, 12.5, "bold");
  const summary = wrapped(ctx, finding.summary, width, 9.2, "normal");
  const evidence = (finding.evidence || []).map(item => wrapped(ctx, "- " + item, width - 8, 8.7, "normal"));
  const notice = finding.notice ? wrapped(ctx, finding.notice, width - 20, 8.7, "bold") : [];
  const question = wrapped(ctx, finding.question, width, 9.2, "normal");
  const noticeHeight = notice.length ? notice.length * 11.5 + 18 : 0;
  const height = 54 + title.length * 16 + summary.length * 12.5 + noticeHeight + evidence.reduce((sum, lines) => sum + lines.length * 11 + 3, 0) + question.length * 12.5 + 48;
  return { title, summary, evidence, notice, noticeHeight, question, height };
}

function drawFindingCard(ctx, page, finding, layout, y, index, total) {
  drawRect(ctx, page, PAGE.left, y, CONTENT_WIDTH, layout.height, COLORS.wash, COLORS.line, 1);
  drawRect(ctx, page, PAGE.left, y, 7, layout.height, statusColor(finding.status));
  let cursor = y + 17;
  drawText(ctx, page, "REVIEW AREA " + (index + 1) + " OF " + total + "  |  " + statusLabel(finding.status) + "  |  " + clean(finding.kicker).toUpperCase(), PAGE.left + 20, cursor, 7.4, "bold", COLORS.muted);
  cursor += 21;
  drawTextLines(ctx, page, layout.title, PAGE.left + 20, cursor, 12.5, 16, "bold", COLORS.ink);
  cursor += layout.title.length * 16 + 3;
  drawTextLines(ctx, page, layout.summary, PAGE.left + 20, cursor, 9.2, 12.5, "normal", COLORS.muted);
  cursor += layout.summary.length * 12.5 + 13;
  if (layout.notice.length) {
    drawRect(ctx, page, PAGE.left + 20, cursor, CONTENT_WIDTH - 40, layout.noticeHeight, [255, 248, 233], [222, 201, 148], 1);
    drawTextLines(ctx, page, layout.notice, PAGE.left + 30, cursor + 9, 8.7, 11.5, "bold", [79, 61, 24]);
    cursor += layout.noticeHeight + 12;
  }
  drawText(ctx, page, "EVIDENCE AND NEXT QUESTION", PAGE.left + 20, cursor, 8.3, "bold", COLORS.forest);
  cursor += 16;

  for (const lines of layout.evidence) {
    drawTextLines(ctx, page, lines, PAGE.left + 23, cursor, 8.7, 11, "normal", COLORS.muted);
    cursor += lines.length * 11 + 3;
  }
  drawTextLines(ctx, page, layout.question, PAGE.left + 20, cursor + 3, 9.2, 12.5, "normal", COLORS.ink);
}

function drawInvestigation(ctx, snapshot) {
  let page = newPage(ctx);
  let y = sectionHeading(ctx, page, "LANDSAGE REVIEW PATH", "How LandSage can move this property question forward.");
  y = drawLines(ctx, page, "The free scan identifies uncertainty. A professional review connects it to the parcel, intended use, official records, and the decision in front of you.", PAGE.left, y, CONTENT_WIDTH, 14, 10, "normal", COLORS.muted) + 15;

  snapshot.investigation.forEach((item, index) => {
    const lines = wrapped(ctx, item, CONTENT_WIDTH - 55, 10, "normal");
    const height = Math.max(42, lines.length * 14 + 18);
    if (y + height > PAGE.height - PAGE.bottom) {
      page = newPage(ctx);
      y = sectionHeading(ctx, page, "LANDSAGE REVIEW PATH - CONTINUED", "Property-specific work worth completing.");
    }
    drawRule(ctx, page, PAGE.left, y, PAGE.width - PAGE.right, COLORS.line);
    page.drawEllipse({
      x: PAGE.left + 15,
      y: PAGE.height - y - 20,
      xScale: 12,
      yScale: 12,
      color: color(ctx, COLORS.sage)
    });
    drawText(ctx, page, String(index + 1), PAGE.left + 15, y + 14, 8, "bold", COLORS.forest, { align: "center" });
    drawTextLines(ctx, page, lines, PAGE.left + 45, y + 11, 10, 14, "normal", COLORS.ink);
    y += height;
  });
}

function drawSourcesAndLimitations(ctx, snapshot) {
  let page = newPage(ctx);
  let y = sectionHeading(ctx, page, "SOURCE RECORD", "Sources, data status, and important limitations.");
  y = smallHeading(ctx, page, "SOURCES CHECKED", y);

  for (const source of snapshot.sources) {
    const title = clean(source.name) + " - " + sourceStatus(source.status);
    const titleLines = wrapped(ctx, title, CONTENT_WIDTH - 16, 9.3, "bold");
    const noteLines = source.note ? wrapped(ctx, source.note, CONTENT_WIDTH - 16, 8.3, "normal") : [];
    const height = titleLines.length * 12 + noteLines.length * 11 + 10;
    if (y + height > PAGE.height - PAGE.bottom) {
      page = newPage(ctx);
      y = sectionHeading(ctx, page, "SOURCE RECORD - CONTINUED", "Sources checked.");
    }
    drawTextLines(ctx, page, titleLines, PAGE.left + 12, y, 9.3, 12, "bold", COLORS.ink);
    y += titleLines.length * 12 + 3;
    if (noteLines.length) {
      drawTextLines(ctx, page, noteLines, PAGE.left + 12, y, 8.3, 11, "normal", COLORS.muted);
      y += noteLines.length * 11;
    }
    y += 10;
  }

  if (y + 90 > PAGE.height - PAGE.bottom) {
    page = newPage(ctx);
    y = sectionHeading(ctx, page, "LIMITATIONS", "Read before relying on this scan.");
  } else {
    y = smallHeading(ctx, page, "LIMITATIONS", y + 6);
  }

  for (const limitation of snapshot.limitations) {
    const lines = wrapped(ctx, "- " + limitation, CONTENT_WIDTH - 12, 8.8, "normal");
    const height = lines.length * 12 + 7;
    if (y + height > PAGE.height - PAGE.bottom - 98) {
      page = newPage(ctx);
      y = sectionHeading(ctx, page, "LIMITATIONS - CONTINUED", "Read before relying on this scan.");
    }
    drawTextLines(ctx, page, lines, PAGE.left + 6, y, 8.8, 12, "normal", COLORS.muted);
    y += height;
  }

  if (y + 102 > PAGE.height - PAGE.bottom) {
    page = newPage(ctx);
    y = sectionHeading(ctx, page, "PROFESSIONAL REVIEW", "When uncertainty could affect the decision.");
  } else {
    y += 15;
  }

  drawRect(ctx, page, PAGE.left, y, CONTENT_WIDTH, 96, COLORS.forestDark);
  drawText(ctx, page, "Turn the open questions into a property decision.", PAGE.left + 20, y + 19, 12, "bold", COLORS.white);
  drawLines(ctx, page, "LandSage can complete the focused research, distinguish known facts from unresolved issues, and identify only the outside confirmation the decision actually requires.", PAGE.left + 20, y + 40, CONTENT_WIDTH - 40, 11, 8.4, "normal", COLORS.paleText);
  const linkText = "Request a professional property review";
  drawText(ctx, page, linkText, PAGE.left + 20, y + 78, 9, "bold", COLORS.sageText);
  addLinkAnnotation(ctx, page, linkText, PAGE.left + 20, y + 78, 9, "bold", REVIEW_URL);
}

function addPageFurniture(ctx, snapshot) {
  const pages = ctx.pdf.getPages();
  pages.forEach((page, index) => {
    if (index === 0) return;
    drawText(ctx, page, "LANDSAGE INSIGHTS  |  PROPERTY SCANNER", PAGE.left, 24, 7.5, "bold", COLORS.forest);
    drawText(ctx, page, clean(snapshot.snapshotId), PAGE.left, PAGE.height - 31, 7.5, "normal", COLORS.muted);
    drawText(ctx, page, (index + 1) + " / " + pages.length, PAGE.width - PAGE.right, PAGE.height - 31, 7.5, "normal", COLORS.muted, { align: "right" });
  });
}

function sectionHeading(ctx, page, eyebrow, title) {
  drawText(ctx, page, clean(eyebrow), PAGE.left, PAGE.top - 8, 8, "bold", COLORS.forest);
  return drawLines(ctx, page, title, PAGE.left, PAGE.top + 22, CONTENT_WIDTH, 25, 21, "bold", COLORS.ink) + 20;
}

function smallHeading(ctx, page, title, y) {
  drawText(ctx, page, clean(title), PAGE.left, y, 8.3, "bold", COLORS.forest);
  return y + 23;
}

function propertyRows(snapshot) {
  const parcelMode = snapshot.analysisMode === "verified_parcel";
  const property = snapshot.property;
  return [
    ["Analysis mode", parcelMode ? "Verified parcel analysis" : "Address-centered preliminary screen"],
    ["County / state", property.jurisdiction],
    ["Address-point source", property.locationBasis],
    ["Boundary basis", property.boundaryStatus],
    ["Parcel number", property.parcelId],
    ["Calculated area", property.acres == null ? null : property.acres + " acres"],
    ["Recorded land use", property.landUse],
    ["Parcel provider address", property.parcelProviderAddress]
  ].map(([label, value]) => [label, value == null ? null : clean(value)]);
}

async function embedDataImage(pdf, dataUrl) {
  const match = /^data:image\/(png|jpe?g);base64,(.+)$/i.exec(dataUrl);
  if (!match) throw new Error("Unsupported map image.");
  const bytes = Uint8Array.from(atob(match[2]), character => character.charCodeAt(0));
  return match[1].toLowerCase() === "png" ? pdf.embedPng(bytes) : pdf.embedJpg(bytes);
}

function addLinkAnnotation(ctx, page, text, x, top, size, style, url) {
  const width = font(ctx, style).widthOfTextAtSize(clean(text), size);
  const bottom = PAGE.height - top - size - 2;
  const annotation = ctx.pdf.context.register(ctx.pdf.context.obj({
    Type: ctx.PDFName.of("Annot"),
    Subtype: ctx.PDFName.of("Link"),
    Rect: [x, bottom, x + width, bottom + size + 5],
    Border: [0, 0, 0],
    A: {
      Type: ctx.PDFName.of("Action"),
      S: ctx.PDFName.of("URI"),
      URI: ctx.PDFString.of(url)
    }
  }));
  page.node.addAnnot(annotation);
}

function wrapped(ctx, value, width, size, style) {
  const selectedFont = font(ctx, style);
  const words = clean(value).split(" ").filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let line = words.shift();

  for (const word of words) {
    const candidate = line + " " + word;
    if (selectedFont.widthOfTextAtSize(candidate, size) <= width) {
      line = candidate;
    } else {
      lines.push(line);
      line = word;
    }
  }
  lines.push(line);
  return lines;
}

function drawLines(ctx, page, value, x, top, width, lineHeight, size, style, textColor) {
  const lines = wrapped(ctx, value, width, size, style);
  drawTextLines(ctx, page, lines, x, top, size, lineHeight, style, textColor);
  return top + Math.max(0, lines.length - 1) * lineHeight + size;
}

function drawTextLines(ctx, page, lines, x, top, size, lineHeight, style, textColor) {
  lines.forEach((line, index) => drawText(ctx, page, line, x, top + index * lineHeight, size, style, textColor));
}

function drawText(ctx, page, value, x, top, size, style, textColor, { align } = {}) {
  const text = clean(value);
  const selectedFont = font(ctx, style);
  let drawX = x;
  const textWidth = selectedFont.widthOfTextAtSize(text, size);
  if (align === "center") drawX -= textWidth / 2;
  if (align === "right") drawX -= textWidth;
  page.drawText(text, {
    x: drawX,
    y: PAGE.height - top - size,
    size,
    font: selectedFont,
    color: color(ctx, textColor)
  });
}

function drawRect(ctx, page, x, top, width, height, fillColor, borderColor, borderWidth = 0) {
  const options = {
    x,
    y: PAGE.height - top - height,
    width,
    height,
    color: color(ctx, fillColor)
  };
  if (borderColor) {
    options.borderColor = color(ctx, borderColor);
    options.borderWidth = borderWidth;
  }
  page.drawRectangle(options);
}

function drawRule(ctx, page, x1, top, x2, lineColor) {
  page.drawLine({
    start: { x: x1, y: PAGE.height - top },
    end: { x: x2, y: PAGE.height - top },
    thickness: .8,
    color: color(ctx, lineColor)
  });
}

function newPage(ctx) {
  return ctx.pdf.addPage([PAGE.width, PAGE.height]);
}

function font(ctx, style) {
  return style === "bold" ? ctx.fonts.bold : ctx.fonts.regular;
}

function color(ctx, values) {
  return ctx.rgb(values[0] / 255, values[1] / 255, values[2] / 255);
}

function statusColor(status) {
  return COLORS[status] || COLORS.unknown;
}

function statusLabel(status) {
  return ({ attention: "MAPPED CONDITION", watch: "WORTH CHECKING", verify: "VERIFY LOCALLY", clear: "NO MAPPED OVERLAP FOUND", unknown: "NOT RESOLVED" })[status] || "REVIEW";
}

function sourceStatus(status) {
  return ({ available: "AVAILABLE", unavailable: "UNAVAILABLE", not_configured: "NOT CONFIGURED", not_found: "NO PARCEL RETURNED" })[status] || clean(status).toUpperCase();
}

function formatDate(value) {
  try {
    return new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
  } catch {
    return clean(value);
  }
}

function number(value) {
  return Number(value).toLocaleString("en-US");
}

function clean(value) {
  return String(value ?? "")
    .replace(/[\u2010-\u2015\u2212]/g, "-")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\u2026/g, "...")
    .replace(/\u2022/g, "-")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}
